import * as React from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import { ReactComponent as CalendarIcon } from '../assets/svg/calendar.svg';

interface DateTimeInputTypes {
	dateTimeString: string;
	onChange?: any;
	excludeFuture?: boolean;
}

export const DateTimeInput: React.FC<DateTimeInputTypes> = ({ dateTimeString, onChange, excludeFuture = false }) => {
	const [dateTimeObject, setDateTimeObject] = React.useState<Date>();
	const [timeValue, setTimeValue] = React.useState<string>('');
	const [amPmValue, setAmPmValue] = React.useState<string>('am');

	const pad = (num: number): string => {
		return num < 10 ? '0' + num : num.toString();
	};

	const isAmPm = (num: number): string => {
		return num < 12 ? 'am' : 'pm';
	};

	const to12hour = (num: number): number => {
		return num > 12 ? num - 12 : num;
	};

	// date comes in as a string: 2022-04-22T11:28:07.1994149+10:00
	// date is returned as a date object
	const parseDate = (str: string) => {
		const parsedDate = moment(str, 'YYYY-MM-DDTHH:mm:ss.SSSSSSSZ');
		const newDate = parsedDate.isValid() ? parsedDate.toDate() : new Date();

		return newDate;
	};

	// date is returned as a string: 2022-04-22T11:28:07
	const returnDateTime = (obj: Date) => {
		// do not return the timezone offset
		const dateStr = moment(obj).format('YYYY-MM-DDTHH:mm:ss');

		if (onChange) {
			onChange(dateStr);
		}
	};

	const formatTime = (obj: Date): string => {
		return `${pad(to12hour(obj.getHours()))}:${pad(obj.getMinutes())}`;
	};

	const handleDateChange = (date: Date) => {
		setDateTimeObject(date);
	};

	const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const timeValue = e.target.value;
		setTimeValue(timeValue);
	};

	const handleTimeBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
		const timeValue = e.target.value;
		const newDate = dateTimeObject ? new Date(dateTimeObject) : new Date();

		if (timeValue) {
			const timeArr = timeValue.split(':');
			let hours = !isNaN(parseInt(timeArr[0])) ? parseInt(timeArr[0]) : 0;
			let minutes = !isNaN(parseInt(timeArr[1])) ? parseInt(timeArr[1]) : 0;

			hours = hours < 0 ? 0 : hours > 23 ? 23 : hours;
			minutes = minutes < 0 ? 0 : minutes > 59 ? 59 : minutes;

			if (amPmValue && amPmValue.toLowerCase() === 'pm' && hours < 12) {
				// 12 hour to 24 hour
				hours += 12;
			} else if (amPmValue && amPmValue.toLowerCase() === 'am' && hours === 12) {
				// 12 midnight is 0
				hours = 0;
			}
			newDate.setHours(hours);
			newDate.setMinutes(minutes);
			newDate.setSeconds(0);
		}

		setDateTimeObject(newDate);
	};

	const handleAmPmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const amPmValue = e.target.value;

		if (dateTimeObject) {
			const newDate = new Date(dateTimeObject);
			if (amPmValue === 'am' && newDate.getHours() >= 12) {
				newDate.setHours(newDate.getHours() - 12);
			} else if (amPmValue === 'pm' && newDate.getHours() < 12) {
				newDate.setHours(newDate.getHours() + 12);
			}

			setDateTimeObject(newDate);
		}
		setAmPmValue(amPmValue);
	};

	React.useEffect(() => {
		const currentDate = parseDate(dateTimeString);

		setDateTimeObject(currentDate);
	}, []);

	React.useEffect(() => {
		if (dateTimeObject) {
			setTimeValue(formatTime(dateTimeObject));
			setAmPmValue(isAmPm(dateTimeObject.getHours()));
			returnDateTime(dateTimeObject);
		} else {
			//pass back the null value
			if (onChange) {
				onChange('');
			}
		}
	}, [dateTimeObject]);

	return (
		<div className="row form-row">
			<div className="col-12 col-sm-6">
				<div className="form-control date-picker">
					<CalendarIcon />
					<DatePicker
						dateFormat="dd/MM/yyyy"
						selected={dateTimeObject}
						onChange={(date: Date) => handleDateChange(date)}
						placeholderText="DD/MM/YYYY"
						className="form-control"
						maxDate={excludeFuture ? new Date() : null}
					/>
				</div>
			</div>
			<div className="col-12 col-sm-6">
				<div className="form-control time-picker" tabIndex={1}>
					<input
						className="form-control"
						type="text"
						value={timeValue}
						onChange={handleTimeChange}
						onBlur={handleTimeBlur}
						placeholder="HH:MM"
					/>
					<label tabIndex={2}>
						<input type="radio" value="am" checked={amPmValue === 'am'} onChange={handleAmPmChange} />
						<span>AM</span>
					</label>
					<label tabIndex={3}>
						<input type="radio" value="pm" checked={amPmValue === 'pm'} onChange={handleAmPmChange} />
						<span>PM</span>
					</label>
				</div>
			</div>
		</div>
	);
};

export default DateTimeInput;
